import React, {Fragment, useEffect, useMemo, useState} from 'react';
import {useSelector, shallowEqual, useDispatch} from 'react-redux';
import usePrevious from "@hooks/useprevious";
import {DELETE_NOTE_REQUEST, GET_SYSTEM_NOTES_REQUEST, SEND_SYSTEM_NOTE_REQUEST} from "@redux/Customer/actions";
import notify from '@utils/notify';
import {Input, Modal} from "reactstrap";
import IcoMoon from "react-icomoon";
import IconSet from "@assets/selection.json";
import {ButtonContainer} from "@pages/reusable-components/styled-components";
import {
    CustomBadge,
    CustomContainer,
    CustomHeader, CustomInput,
    CustomReactStrapInput,
    FleetButton,
    ProfileImage,
    RequiredItemsBadge
} from "@pages/new-design-components/custom-components";
import { formatTimestamp } from '@constants/index';
import { hasRole } from 'views/policies/PermissionPolicy';

interface CustomerState {
    sendSystemNoteSuccess: boolean;
    sendSystemNoteError: boolean;
    deleteNoteSuccess: boolean;
    deleteNoteError: boolean;
    systemNotes: any[];
}
interface GetState {
    roles: string[];
}
interface RootState {
    customer: CustomerState;
    getState: GetState;
}
interface IProps {
    noteTabs: any[];
    setError: (value: any) => void;
    error: any;
    disabled?: boolean;
    profileDataId: number;
    customerId: number;
    noteAboutCustomer?: boolean;
    updateCustomer?: (value: any) => void;
    initialValue?: string;
}
const NotesComponent: React.FC<IProps> = ({   noteTabs,
                                                    setError,
                                                    error,
                                                    disabled = false,
                                                    profileDataId,
                                                    customerId,
                                                    noteAboutCustomer = false,
                                                    updateCustomer,
                                                    initialValue = ''}) => {

    const dispatch = useDispatch();

    const {
        sendSystemNoteSuccess,
        sendSystemNoteError,
        deleteNoteSuccess,
        deleteNoteError,
        systemNotes
    } = useSelector((state: RootState) => state.customer, shallowEqual);

    const roles = useSelector((state: RootState) => state.getState.roles);

    // @ts-ignore
    const sendSystemNoteSuccessPrev = usePrevious<boolean>(sendSystemNoteSuccess);
    // @ts-ignore
    const sendSystemNoteErrorPrev = usePrevious<boolean>(sendSystemNoteError);
    // @ts-ignore
    const deleteNoteSuccessPrev = usePrevious<boolean>(deleteNoteSuccess);
    // @ts-ignore
    const deleteNoteErrorPrev = usePrevious<boolean>(deleteNoteError);

    const [showModal, setShowModal] = useState<boolean>(false);
    const [customerNote, setCustomerNote] = useState<string>('');
    const [deleteId, setDeleteId] = useState<number | null>(null);
    const [activeNoteTab, setActiveNoteTab] = useState<string>('all');
    const [newMessage, setNewMessage] = useState<{ message: string } | null>(null);

    useEffect(() => {
        setCustomerNote(initialValue);
    }, [initialValue]);

    useEffect(() => {
        if (sendSystemNoteSuccess && sendSystemNoteSuccessPrev === false) {
            dispatch({
                type: GET_SYSTEM_NOTES_REQUEST,
                payload: { id: customerId, type: 'debt' },
            });
            setNewMessage({ message: '' });
            notify.success('Sent successfully');
        }
    }, [sendSystemNoteSuccess, sendSystemNoteSuccessPrev, dispatch, customerId]);

    useEffect(() => {
        if (sendSystemNoteError && sendSystemNoteErrorPrev === false) {
            notify.warning('Something went wrong please try again later!');
        }
    }, [sendSystemNoteError, sendSystemNoteErrorPrev]);

    useEffect(() => {
        if (deleteNoteSuccess && deleteNoteSuccessPrev === false) {
            dispatch({
                type: GET_SYSTEM_NOTES_REQUEST,
                payload: { id: customerId, type: 'debt' },
            });
            setNewMessage({ message: '' });
            notify.success('Deleted successfully');
        }
    }, [deleteNoteSuccess, deleteNoteSuccessPrev, dispatch, customerId]);

    useEffect(() => {
        if (deleteNoteError && deleteNoteErrorPrev === false) {
            notify.warning('Something went wrong please try again later!');
        }
    }, [deleteNoteError, deleteNoteErrorPrev]);

    const sendMessage = ({newMessage,activeNoteTab}) => {
        let obj = {
            'creator_id': profileDataId,
            'creator_type': 'User',
            'note': newMessage.message,
            'write_log': true,
        }
        if (activeNoteTab === 'all' || activeNoteTab === 'Customer') {
            obj['noteable_id'] =customerId
            obj['noteable_type'] = 'Customer'
        } else if (activeNoteTab === 'Deal') {
            obj['noteable_id'] = customerId
            obj['noteable_type'] = 'deal'
        } else {
            obj['noteable_type'] = activeNoteTab
            obj['noteable_id'] = customerId
        }
        dispatch({
            type: SEND_SYSTEM_NOTE_REQUEST,
            payload: obj
        });
    }

    const checkAndSend = () => {
        if (newMessage?.message.length > 3) {
            sendMessage({newMessage,activeNoteTab});
            setError({...error,message: {show: false,message: ''}})
        } else {
            setError({...error,message: {show: true,message: 'Too short. Please write more than 3 letters'}})
        }
    }
    const filterNotes = useMemo(()=>{
        if (activeNoteTab === 'all') {
            return systemNotes
        } else if(activeNoteTab === 'Deal') {
            return  systemNotes.filter(note=>note.noteable_type === 'deal')
        }  else if(activeNoteTab === 'DebtRecovery') {
            return  systemNotes.filter(note=>note.noteable_type === 'DebtRecovery')
        }  else {
            return  systemNotes.filter(note=>note.noteable_type === activeNoteTab)
        }
    },[activeNoteTab,systemNotes])
    const deleteNote = (id: number | string) => {
        dispatch({
            type: DELETE_NOTE_REQUEST,
            payload: { id: id }
        });
    }

    return (  <>   <Modal
        isOpen={showModal}
        toggle={() => {
            setShowModal(false)
            setDeleteId(null)
        }}
        className="modal-dialog-centered"
    >
        <div className="modal-header">
            <h6 className="modal-title main-color">Delete Note</h6>
            <button
                aria-label="Close"
                className="close"
                data-dismiss="modal"
                type="button"
                onClick={() => {
                    setShowModal(false)
                    setDeleteId(null)
                }}
            >
                <span aria-hidden={true}>×</span>
            </button>
        </div>
        <div className="modal-body">
            Are you Sure ?
        </div>
        <div className="modal-footer justify-content-center">
            <ButtonContainer
                onClick={() => {
                    setShowModal(false)
                    setDeleteId(null)
                }}
            >
                {`Close`}
            </ButtonContainer>
            <ButtonContainer
                reverse
                onClick={()=>{
                    setShowModal(false)
                    deleteNote(deleteId)
                }}
            >
                {`Delete`}
            </ButtonContainer>

        </div>
    </Modal>
        <div className="d-flex flex-wrap justify-content-between mb-2 ">
            <CustomHeader weight={'700'} color={'#000'} fontSize={'20px'}>Key info</CustomHeader>
            {noteAboutCustomer && <div className={'w-75 d-flex g-5 align-items-center'}>

                <Input
                    className="form-control-alternative edit-event--title"
                    placeholder="Note"
                    type="textarea"
                    rows="8"
                    value={customerNote} onChange={(e) => setCustomerNote(e.target.value)}
                />

                {initialValue !== customerNote &&
                    <>
                        <IcoMoon
                            className={'cursor-pointer'}
                            iconSet={IconSet}
                            onClick={()=>{setCustomerNote(initialValue)}}
                            icon={'close-svgrepo-com'}
                            size={24}
                        />
                        <IcoMoon
                            className={'cursor-pointer'}
                            iconSet={IconSet}
                            onClick={()=>{updateCustomer(customerNote)}}
                            icon={'ic_checkmark'}
                            size={24}
                        />
                    </>
                }
            </div>}
        </div>

        <div className={'d-flex g-10 pb-3 flex-wrap'}>
            {
                noteTabs.map((tab,index) => {
                    return (
                        <Fragment key={'n_'+index}>
                            <RequiredItemsBadge  fontSize={'14px'} width={'120px'}  type={tab.key} onClick={()=> {
                                setActiveNoteTab(tab.key);
                            }
                            } active={activeNoteTab === tab.key}>
                                {tab.label}
                            </RequiredItemsBadge>
                        </Fragment>
                    )
                })
            }
        </div>

        <CustomContainer backgroundColor={'#FFF'} borderRadius="8px" padding="10px 16px">
            <CustomHeader weight={'600'} color={'#000'} fontSize={'14px'}>Create Note:</CustomHeader>
            <div className={'d-flex g-10'}>
                <ProfileImage
                    src={require("assets/images/taskEdit/person.png")}
                    alt=""/>

                <form
                    className={'w-100'}
                    onSubmit={(e) => {
                        e.preventDefault();
                        checkAndSend()
                    }}
                >
                    <CustomReactStrapInput
                        onChange={(e) => {
                            setNewMessage({
                                ...newMessage,
                                message: e.target.value
                            });
                        }}
                        borderBottom={error.message.show ? '1px solid red' : '1px solid #0000008C'}
                        radius={'none'}
                        value={newMessage?.message}
                    />
                    {error.message.show && <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}> {error.message.message}</div>}

                    <button type="submit" style={{ display: 'none' }} />
                </form>
                <FleetButton  height={'43px'}
                              width={'114px'}
                              backgroundColor={'#000'}
                              color={'#FFF'}
                              disabled={disabled}
                              fontSize={'14px'}
                              radius={'24px'} onClick={checkAndSend}> Save </FleetButton>
            </div>
        </CustomContainer>
        {
            filterNotes.map((item,idx)=>{
                return <Fragment key={`nt_${idx}`}>
                    <CustomContainer backgroundColor={'#FFF'} borderRadius="8px" padding="10px 16px" margin={'6px 0px 6px 0px'}>
                        <div className={'d-flex justify-content-between flex-wrap'}>
                            <div>
                                <CustomHeader weight={'600'} color={'#000'} fontSize={'14px'}>{item.noteable?.name || item.noteable?.full_name}</CustomHeader>
                                <CustomBadge type={item?.noteable_type}>
                                    {item?.noteable_type === '\\App\\User' ? 'Admin' : item?.noteable_type === 'CustomerInvoiceDebt' ?  item?.noteable?.xero_invoice_number : item?.noteable_type }
                                </CustomBadge>
                            </div>
                            <div className={'d-flex g-5'}>
                                <CustomHeader weight={'600'} color={'#000'} fontSize={'14px'}>{item.creator?.name || item.creator?.full_name}</CustomHeader>
                                <CustomHeader weight={'400'} color={'#8D8D8D'} fontSize={'12px'}>
                                    {formatTimestamp(item?.created_at)}
                                </CustomHeader>
                                {
                                    hasRole(roles, 'dealer_admin') &&
                                    <IcoMoon
                                        className={'cursor-pointer'}
                                        iconSet={IconSet}
                                        icon={'trash-alt'}
                                        size={24}
                                        onClick={() => {
                                            setShowModal(true)
                                            setDeleteId(item.id)
                                        }}
                                    />
                                }
                            </div>

                        </div>
                        <CustomHeader weight={'400'} color={'#979797'} fontSize={'14px'}>{item.note}</CustomHeader>
                    </CustomContainer>
                </Fragment>
            })
        }


    </>)
}

export default NotesComponent;